import axios from "axios";
import Link from "next/link";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  UseInfiniteQueryResult,
  useInfiniteQuery,
  useQuery,
} from "react-query";
import { useRouter } from "next/router";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";

import { NextSeo } from "next-seo";
import VoiceImageJs from "../VoiceImage";
import debounce from "lodash/debounce";
import { getIdToken } from "../../utils/firebaseAuth";
import {
  JamCardSkeleton,
  JamVoiceCardSkeleton,
  JamVoiceCard,
  SectionHeading,
} from "../home/JamCards";
import VoiceSortTabs, {
  VOICE_SORTS,
  VoiceSortKey,
} from "../voices/VoiceSortTabs";

// VoiceImage is untyped JS whose props all read as required; every one is
// optional in practice.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const VoiceImage = VoiceImageJs as unknown as React.FC<any>;

// URL `?sort=` key -> /api/model `sortBy`. "hot" is the default and is left
// out of the URL.
const sortOptions = Object.fromEntries(
  Object.entries(VOICE_SORTS).map(([key, value]) => [key, value.sortBy]),
) as Record<VoiceSortKey, string>;

interface Trending {
  isTrending: boolean;
  position: number | undefined;
}

export interface ApiListResponse<T> {
  data: T[];
  paging: {
    hasMore: boolean;
  };
  collections?: any[];
}

export interface Model {
  _id: string;
  artistname: string;
  uses: number;
  user: {
    username: string;
  };
  image: {
    url: string;
    blurhash?: string;
  };
  isLive: boolean;
  isPublic: boolean;
  urlParam: string;
  badges?: string[];
  likes?: number;
  trending: Trending;
  description?: string;
  createdAt: Date;
  updatedAt: Date;
}

type PaginationParams = {
  page: number;
  search: string;
  sortBy: string;
  sortOrder: -1 | 1; // Assuming sortOrder can only be -1 or 1
  limit: number;
  hasMore: boolean;
};

type SortOption = VoiceSortKey;

// Add a new interface for Badge
interface Badge {
  badge: string;
  count: number;
  emoji?: string;
  category?: string;
}

// Add a hook to fetch badges
function useBadges(search: string = "", initial?: Badge[] | null) {
  return useQuery<{ data: Badge[] }>(
    ["badges", search],
    async () => {
      const res = await axios.get("/api/model/getBadges", {
        params: { search },
      });
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
      // Server-rendered with the page (pages/models), so no pop-in.
      initialData: initial && !search ? { data: initial } : undefined,
    },
  );
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function useSearch(
  initialModels?: any[] | null,
  initialCollections?: any[] | null,
): [UseInfiniteQueryResult<ApiListResponse<Model>>, any] {
  /* eslint-enable @typescript-eslint/no-explicit-any */
  const router = useRouter();
  const { q, filter, sort: sortQuery = "hot" } = router.query;

  const [searchInternal, setSearchInternal] = useState((q as string) || "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchInternal);

  const sort = (
    Array.isArray(sortQuery) ? sortQuery[0] : sortQuery
  ) as SortOption;

  const validSort: SortOption = Object.prototype.hasOwnProperty.call(
    sortOptions,
    sort,
  )
    ? sort
    : "hot";

  // Debounce the search state update with a shorter delay for UI updates
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
      }, 250),
    [],
  );

  // Function to update both internal state immediately and trigger debouncedSetSearch
  const setSearch = useCallback(
    (value: string) => {
      setSearchInternal(value);
      debouncedSetSearch(value);
    },
    [debouncedSetSearch],
  );

  // Cancel debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  const setSort = (newSort: string) => {
    if (newSort === sort) return;
    if (newSort === "hot") {
      // Preserve other parameters when removing the sort parameter
      const newQuery = { ...router.query };
      delete newQuery.sort;
      router.push({
        pathname: router.pathname,
        query: newQuery,
      });
      return;
    }
    router.push({
      pathname: router.pathname,
      query: { ...router.query, sort: newSort },
    });
  };

  const setFilter = (newFilter: string) => {
    if (newFilter === filter) return;
    if (newFilter === "all") {
      const newQuery = { ...router.query };
      delete newQuery.filter;
      router.push({
        pathname: router.pathname,
        query: newQuery,
      });
      return;
    }
    router.push({
      pathname: router.pathname,
      query: { ...router.query, filter: newFilter },
    });
  };

  const limit = 24;

  async function fetchModelsSortedByUses({ pageParam = 1 }): Promise<{
    paging: PaginationParams;
    data: Model[];
  }> {
    const res = await axios.get(`/api/model`, {
      params: {
        filter,
        search: debouncedSearch, // Use the debounced search value for API calls
        limit,
        page: pageParam,
        sortOrder: "desc",
        sortBy: sortOptions[validSort],
      },
    });

    return res.data;
  }

  // The SSR page is always the default "hot" sort with no search, so it is
  // only a valid seed for that query - seeding ?sort=new with it showed
  // trending voices under a "Newest" label until something refetched.
  const ssrInitialData =
    initialModels && validSort === "hot" && !debouncedSearch
      ? {
          pages: [
            {
              // SSR fetches 25 to learn whether there is a page 2; page 2 is
              // fetched at limit 24, so the 25th would render twice.
              data: initialModels.slice(0, 24),
              paging: {
                page: 1,
                hasMore: initialModels.length > 24,
                search: "",
                sortBy: "usesLast24Hours",
                sortOrder: -1 as const,
                limit: 24,
              },
              collections: initialCollections || [],
            },
          ],
          pageParams: [1],
        }
      : undefined;

  const models = useInfiniteQuery(
    ["all-models", debouncedSearch, filter, validSort], // Use debounced search in query key
    fetchModelsSortedByUses,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.paging.hasMore ? lastPage.paging.page + 1 : undefined;
      },
      // Don't refetch on window focus to avoid unexpected refreshes
      refetchOnWindowFocus: false,
      initialData: ssrInitialData,
    },
  );

  return [
    models,
    {
      value: searchInternal,
      setSearch,
      sort: validSort,
      setSort,
      filter,
      setFilter,
    },
  ];
}

const capitalizeFirstLetter = (value: string) => {
  return value?.charAt(0)?.toUpperCase() + value?.slice(1);
};

// How many category pills show before "More categories". Ordered by count
// here rather than trusting the cache's order.
const PILL_LIMIT = 14;

const ModelSearchPage = ({
  initialModels,
  initialCollections,
  initialBadges,
  noindex = false,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialModels?: any[] | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialCollections?: any[] | null;
  initialBadges?: Badge[] | null;
  // Decided by pages/models.tsx; repeated here because next-seo writes a
  // robots tag on every NextSeo and the last one rendered wins.
  noindex?: boolean;
}) => {
  const router = useRouter();

  const [showAllPills, setShowAllPills] = useState(false);
  // Collections are one scrolling row so the voices grid stays the page's
  // main content (a 4-column grid of 20+ collections pushed it off-screen).
  const collectionsRef = useRef<HTMLDivElement>(null);
  const scrollCollections = (dir: 1 | -1) => {
    const el = collectionsRef.current;
    if (el)
      el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };

  const [models, search] = useSearch(initialModels, initialCollections);

  // Fetch badges
  const { data: badgesData } = useBadges("", initialBadges);

  const sortedBadges = useMemo(
    () =>
      [...(badgesData?.data || [])]
        .filter((b) => b?.badge)
        .sort((a, b) => (b.count || 0) - (a.count || 0)),
    [badgesData?.data],
  );

  const visibleBadges = useMemo(() => {
    if (showAllPills) return sortedBadges;
    const top = sortedBadges.slice(0, PILL_LIMIT);
    // Keep the active filter visible even when it isn't a top category.
    const active = sortedBadges.find((b) => b.badge === search.filter);
    if (active && !top.includes(active)) top.push(active);
    return top;
  }, [sortedBadges, showAllPills, search.filter]);

  // Infinite scroll off a sentinel just below the grid, fetched 1500px
  // early. The old window scroll handler fired 800px from the bottom of the
  // document - and on phones the footer alone is taller than that, so it
  // was already on screen when the next page pushed it down: 0.4-0.5 CLS
  // for anyone who scrolled to the end of the first page (2026-10-10 probe).
  // While more pages exist the sentinel is a screen tall, so the footer
  // stays off screen until the last page is in.
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage, isError } = models;
  useEffect(() => {
    const el = loadMoreRef.current;
    // After a failed page, stop: re-observing would refetch in a loop.
    if (!el || !hasNextPage || isError) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "0px 0px 1500px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isError]);

  const updateQuery = useCallback(
    (newValue: string) => {
      const newQuery = { ...router.query };
      if (newValue) {
        newQuery.q = newValue;
      } else {
        delete newQuery.q;
      }
      router.push(
        {
          pathname: router.pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );
  const debouncedUpdateQuery = useMemo(
    () => debounce(updateQuery, 500),
    [updateQuery],
  );

  const title = `${
    search?.filter ? `${capitalizeFirstLetter(search?.filter)} ` : ""
  }AI Voice Library | Jammable`;

  const description = `${
    search?.filter ? `${capitalizeFirstLetter(search?.filter)} ` : "Jammable "
  }AI Voices. ${
    search?.filter ? `${capitalizeFirstLetter(search?.filter)} ` : ""
  }AI Cover Song Maker. Text To Speech. Most Advanced AI Technology. 50,000+ AI Cover Voices.`;

  const filterLabel = search.filter
    ? capitalizeFirstLetter(String(search.filter))
    : "";
  const collections = models?.data?.pages[0]?.collections || [];
  const firstPage = models?.data?.pages[0]?.data;

  const pillClass = (active: boolean) =>
    `tw-flex-shrink-0 tw-cursor-pointer tw-whitespace-nowrap tw-rounded-full tw-border-2 tw-border-solid tw-px-3.5 tw-py-[7px] tw-text-sm tw-font-extrabold tw-capitalize tw-no-underline ${
      active
        ? "tw-border-jam-ink tw-bg-jam-ink tw-text-white"
        : "tw-border-jam-line tw-bg-white tw-text-jam-ink hover:tw-border-jam-purple"
    }`;

  return (
    <div className="tw-bg-white tw-text-jam-ink">
      {/* Title and description only, plus the robots value models.tsx passed
          down. canonical / og:url belong to pages/models.tsx: this used to
          render after it and replace its hub canonical with a
          self-referencing ?filter=/?q= URL (unencoded, so "public figure"
          shipped as a canonical with a space in it). */}
      <NextSeo
        title={title}
        description={description}
        noindex={noindex}
        openGraph={{
          title: title,
          description: description,
        }}
      />
      <main className="tw-mx-auto tw-flex tw-w-full tw-max-w-[1280px] tw-flex-col tw-gap-5 tw-px-4 tw-pb-12 tw-pt-2 md:tw-px-8 md:tw-pt-4">
        <div className="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row md:tw-items-end md:tw-justify-between md:tw-gap-6">
          <div>
            <h1 className="jam-display tw-m-0 tw-text-[36px] tw-font-extrabold tw-leading-none tw-tracking-[-0.04em] md:tw-text-[52px] md:tw-tracking-[-0.045em]">
              Voice library
            </h1>
            <p className="tw-m-0 tw-mt-1.5 tw-text-sm tw-font-bold tw-text-jam-muted md:tw-text-[15px]">
              93,000+ voices. Tap one to hear it, or make a cover straight away.
            </p>
          </div>
          <Link
            href="/train"
            className="tw-self-start tw-whitespace-nowrap tw-rounded-full tw-bg-jam-ink tw-px-6 tw-py-3.5 tw-text-[15px] tw-font-extrabold tw-text-white tw-no-underline hover:tw-text-white md:tw-self-auto"
          >
            Train your own voice
          </Link>
        </div>

        <label className="tw-flex tw-items-center tw-gap-3 tw-rounded-full tw-bg-jam-lav tw-px-5 tw-py-3.5 md:tw-px-6 md:tw-py-[18px]">
          <Search
            size={20}
            strokeWidth={2.5}
            className="tw-flex-shrink-0 tw-text-jam-muted"
          />
          <input
            aria-label="Search voices"
            value={search.value}
            onChange={(e) => {
              const newValue = e.target.value;
              search.setSearch(newValue);
              debouncedUpdateQuery(newValue);
            }}
            placeholder={
              filterLabel
                ? `Search ${filterLabel.toLowerCase()} voices`
                : "Search voices"
            }
            className="tw-w-full tw-min-w-0 tw-border-0 tw-bg-transparent tw-text-[15px] tw-font-bold tw-text-jam-ink tw-outline-none placeholder:tw-text-jam-muted md:tw-text-[17px]"
            autoComplete="off"
            spellCheck={false}
          />
        </label>

        {sortedBadges.length > 0 && (
          <nav
            aria-label="Voice categories"
            className="tw--mx-4 tw-flex tw-gap-2 tw-overflow-x-auto tw-px-4 md:tw-mx-0 md:tw-flex-wrap md:tw-overflow-visible md:tw-px-0 [&::-webkit-scrollbar]:tw-hidden"
          >
            <button
              type="button"
              onClick={() => search.setFilter("all")}
              className={pillClass(!search.filter)}
            >
              All
            </button>
            {visibleBadges.map((badge) => (
              <button
                key={badge.badge}
                type="button"
                onClick={() => search.setFilter(badge.badge)}
                className={pillClass(search.filter === badge.badge)}
              >
                {badge.badge}
              </button>
            ))}
            {sortedBadges.length > PILL_LIMIT && (
              <button
                type="button"
                onClick={() => setShowAllPills((v) => !v)}
                className="tw-flex-shrink-0 tw-cursor-pointer tw-whitespace-nowrap tw-border-0 tw-bg-transparent tw-px-3 tw-py-2 tw-text-sm tw-font-extrabold tw-text-jam-purple"
              >
                {showAllPills ? "Fewer categories" : "More categories"}
              </button>
            )}
          </nav>
        )}

        {collections.length > 0 && (
          <section className="tw-flex tw-flex-col tw-gap-3">
            <SectionHeading
              title="Collections"
              aside={
                collections.length > 4 ? (
                  <div className="tw-hidden tw-gap-2 md:tw-flex">
                    {([-1, 1] as const).map((dir) => (
                      <button
                        key={dir}
                        type="button"
                        aria-label={
                          dir < 0 ? "Previous collections" : "More collections"
                        }
                        onClick={() => scrollCollections(dir)}
                        className="tw-flex tw-h-10 tw-w-10 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-lav tw-text-jam-ink tw-transition-colors hover:tw-bg-jam-line"
                      >
                        {dir < 0 ? (
                          <ChevronLeft size={18} strokeWidth={2.5} />
                        ) : (
                          <ChevronRight size={18} strokeWidth={2.5} />
                        )}
                      </button>
                    ))}
                  </div>
                ) : null
              }
            />
            <div className="tw-relative tw--mx-4 md:tw-mx-0">
              <div
                ref={collectionsRef}
                className="tw-flex tw-snap-x tw-gap-2.5 tw-overflow-x-auto tw-px-4 tw-pb-1 md:tw-px-0 [&::-webkit-scrollbar]:tw-hidden"
              >
                {collections.map((collection) => (
                  <Link
                    key={collection._id}
                    href={`/collection/${collection._id}`}
                    onClick={async () => {
                      try {
                        const lastSearchId =
                          sessionStorage.getItem("lastSearchId");
                        if (!lastSearchId) return;

                        const token = await getIdToken();

                        await axios.post(
                          "/api/search/c",
                          {
                            searchId: lastSearchId,
                            collectionId: collection._id,
                          },
                          {
                            headers: token
                              ? { Authorization: token }
                              : undefined,
                          },
                        );
                      } catch (error) {}
                    }}
                    className="tw-flex tw-w-[230px] tw-flex-shrink-0 tw-snap-start tw-items-center tw-gap-3 tw-rounded-[22px] tw-bg-jam-lav tw-p-2.5 tw-pr-3.5 tw-text-jam-ink tw-no-underline tw-transition-colors hover:tw-bg-jam-line"
                  >
                    <div className="tw-relative tw-h-[52px] tw-w-[52px] tw-flex-shrink-0 tw-overflow-hidden tw-rounded-[16px] tw-bg-jam-line">
                      {collection?.image?.url && (
                        <VoiceImage
                          model={collection}
                          fill
                          sizes="52px"
                          className="tw-object-cover"
                        />
                      )}
                    </div>
                    <div className="tw-min-w-0">
                      <div className="tw-line-clamp-1 tw-text-[15px] tw-font-extrabold">
                        {collection.title}
                      </div>
                      <div className="tw-text-xs tw-font-bold tw-text-jam-muted">
                        {collection.modelsCount} voices
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              {/* Fade hints that the row scrolls (desktop; phones swipe). */}
              <div className="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 tw-hidden tw-w-16 tw-bg-gradient-to-l tw-from-white md:tw-block" />
            </div>
          </section>
        )}

        <section className="tw-flex tw-flex-col tw-gap-3.5">
          <div className="tw-flex tw-flex-col tw-gap-3 md:tw-flex-row md:tw-items-center md:tw-justify-between">
            <SectionHeading
              title={filterLabel ? `${filterLabel} voices` : "All voices"}
            />
            <VoiceSortTabs
              value={search.sort}
              onChange={(key) => search.setSort(key)}
            />
          </div>

          {models?.isLoading && (
            <div className="tw-grid tw-grid-cols-3 tw-gap-2.5 sm:tw-grid-cols-4 md:tw-grid-cols-6 md:tw-gap-3.5">
              {Array.from({ length: 12 }).map((_, i) => (
                <JamVoiceCardSkeleton key={i} />
              ))}
            </div>
          )}

          {!models.isLoading && firstPage?.length === 0 && (
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-rounded-[28px] tw-bg-jam-lav tw-px-6 tw-py-12 tw-text-center">
              <div className="jam-display tw-text-2xl tw-font-extrabold">
                No voices found
              </div>
              <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-jam-muted">
                Try another name, or train the voice yourself.
              </p>
              <Link
                href="/train"
                className="tw-rounded-full tw-bg-jam-purple tw-px-6 tw-py-3 tw-text-sm tw-font-extrabold tw-text-white tw-no-underline hover:tw-text-white"
              >
                Train your own voice
              </Link>
            </div>
          )}

          {firstPage && firstPage.length > 0 && (
            <div className="tw-grid tw-grid-cols-3 tw-gap-2.5 sm:tw-grid-cols-4 md:tw-grid-cols-6 md:tw-gap-3.5">
              {models.data?.pages.map((page) =>
                page.data.map((model) => (
                  <JamVoiceCard model={model} key={model._id} />
                )),
              )}
              {/* The next page arrives where it will sit, like the first. */}
              {models.isFetchingNextPage &&
                Array.from({ length: 6 }).map((_, i) => (
                  <JamVoiceCardSkeleton key={`next-${i}`} />
                ))}
            </div>
          )}

          {models.hasNextPage && (
            <div
              ref={loadMoreRef}
              className="tw-flex tw-min-h-[100vh] tw-items-start tw-justify-center tw-py-2"
            >
              {/* While fetching, the skeleton cards above show it. */}
              {!models.isFetchingNextPage && (
                <button
                  type="button"
                  onClick={() => models.fetchNextPage()}
                  className="tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-jam-lav tw-px-[22px] tw-py-3 tw-text-sm tw-font-extrabold tw-text-jam-ink"
                >
                  Load more voices
                </button>
              )}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default ModelSearchPage;
